import type { RenderoniEngine } from 'renderoni';

/**
 * The browser frame loop. It drives Renderoni's own fixed-tick clock (advancePresentation → step),
 * then lets the game render. It replaces engine.start() because the game needs things that loop does
 * not offer: pausing (menus and screenshot tests), a time scale (hitstop), and a headless engine that
 * is swapped for a fresh one on restart while the renderer stays.
 * Gameplay still runs in whole fixed ticks, so determinism is unaffected.
 */
export class FrameDriver {
  paused = false;
  /** Scales how fast simulated time passes (hitstop); never changes the tick length. */
  timeScale = 1;
  frame = 0;
  private last = 0;
  private rafId: number | null = null;

  constructor(
    /** The current simulation engine (a restart swaps in a fresh one). */
    private readonly engine: () => RenderoniEngine,
    private readonly render: () => void,
    private readonly onFrame: (dt: number, alpha: number) => void,
    /** Called before any ticks this frame, so input reaches the simulation without a frame of lag. */
    private readonly beforeStep: () => void = () => {},
  ) {}

  start(): void {
    if (this.rafId !== null) return;
    this.last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.25, Math.max(0, (now - this.last) / 1000));
      this.last = now;
      this.tick(dt);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  /** One presentation frame: advance simulation (unless paused), update views, render. */
  tick(dt: number): void {
    this.frame += 1;
    if (!this.paused) {
      this.beforeStep();
      const engine = this.engine();
      const ticks = engine.clock.advancePresentation(dt * this.timeScale);
      if (ticks > 0) engine.step(ticks);
    }
    this.onFrame(dt, this.engine().clock.alpha);
    this.render();
  }
}

const tmpPos: [number, number, number] = [0, 0, 0];
const tmpQuat: [number, number, number, number] = [0, 0, 0, 1];

/** Interpolated pose of an entity between the last two fixed ticks. Returns false if missing. */
export function interpolatedPose(
  engine: RenderoniEngine,
  id: string,
  alpha: number,
  out: { position: { set(x: number, y: number, z: number): unknown }; quaternion: { set(x: number, y: number, z: number, w: number): unknown } },
): boolean {
  const entity = engine.entities.get(id);
  if (!entity || entity.slot === undefined) return false;
  if (engine.tick === 0) {
    // No tick has filled the interpolation buffers yet: use the spawn pose.
    const [px, py, pz] = entity.position;
    const [qx, qy, qz, qw] = entity.quaternion;
    out.position.set(px, py, pz);
    out.quaternion.set(qx, qy, qz, qw);
    return true;
  }
  engine.transformPipeline.interpolate(entity.slot, alpha, tmpPos, tmpQuat);
  out.position.set(tmpPos[0], tmpPos[1], tmpPos[2]);
  out.quaternion.set(tmpQuat[0], tmpQuat[1], tmpQuat[2], tmpQuat[3]);
  return true;
}
